import React, { Fragment, useEffect, useState } from "react";
import styles from "./styles.module.css";
import TuneIcon from "@mui/icons-material/Tune";

const TableView = ({
  columns,
  data,
  handleOrder,
  columnWidths,
  tableClassName,
  tableMinWidth,
  stickyActions = false,
  onStatusHeaderClick,
  statusHeaderRef,
  onCategoriaHeaderClick,
  categoriaHeaderRef,
  /** Opt-in: força a coluna "Ações" mesmo com data=[]. undefined preserva o comportamento
   * de sempre (inferido de data.some(...)) — ver comentário em TableCard/index.js. */
  hasActions,
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [order, setOrder] = useState({ lastKey: "", order: "asc" });

  const newData = data.map(({ botoes, ...rest }) => rest);
  const rows = data.length > 0 ? Object.keys(newData[0]) : "";

  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // Fecha a "abinha" de opções sempre que a lista visível muda (ex.: paginação)
  useEffect(() => {
    setExpandedRow(null);
  }, [data]);

  const handleArrows = (index) => {
    const findIndex = rows.findIndex((item) => item === order.lastKey);
    if (findIndex === index) {
      switch (order.order) {
        case "asc":
          return <span style={{ fontSize: "10px", opacity: 0.3 }}>▼</span>;
        case "desc":
          return <span style={{ fontSize: "10px", opacity: 0.3 }}>▲</span>;
        default:
          break;
      }
    } else {
      return <span style={{ fontSize: "10px", opacity: 0.3 }}>▼</span>;
    }
  };

  const hasBotoes = hasActions ?? data.some((item) => item.hasOwnProperty("botoes"));

  // Função para obter largura da coluna
  const getColumnWidth = (columnIndex, columnName) => {
    if (columnWidths && columnWidths[columnIndex] !== undefined) {
      return { width: columnWidths[columnIndex], minWidth: columnWidths[columnIndex], maxWidth: columnWidths[columnIndex] };
    }
    if (columnWidths && columnWidths[columnName]) {
      return { width: columnWidths[columnName], minWidth: columnWidths[columnName], maxWidth: columnWidths[columnName] };
    }
    return {};
  };

  return (
    <>
      <table
        className={tableClassName ? `${styles.accordion_table} ${tableClassName}` : styles.accordion_table}
        style={tableMinWidth ? { minWidth: tableMinWidth } : undefined}
      >
        <thead>
          <tr>
            {columns.map((item, i) => {
              const isSaldo = item === "Saldo";
              const isCNPJ = item === "CNPJ" || (data.length > 0 && rows[i] === "cnpj");
              const isStatus = item === "Status" && onStatusHeaderClick;
              const isCategoria = (item === "Categoria " || item === "Categoria") && onCategoriaHeaderClick;
              const alignStyle = isSaldo ? { textAlign: "right" } : {};
              const widthStyle = getColumnWidth(i, item);
              const sortable = !isCNPJ && !isStatus && !isCategoria;
              const getRef = () => {
                if (isStatus) return statusHeaderRef;
                if (isCategoria) return categoriaHeaderRef;
                return undefined;
              };
              const getOnClick = () => {
                if (isStatus) return onStatusHeaderClick;
                if (isCategoria) return onCategoriaHeaderClick;
                if (isCNPJ) return undefined;
                if (data.length > 0) return () => handleOrder(rows[i], order, setOrder);
                return undefined;
              };
              return (
                <th
                  key={item}
                  ref={getRef()}
                  onClick={getOnClick()}
                  style={{ ...alignStyle, ...widthStyle, cursor: isCNPJ ? "default" : "pointer" }}
                >
                  <div className={styles.colunas} style={alignStyle}>
                    {item}
                    {sortable && data.length > 0 && handleArrows(i)}
                  </div>
                </th>
              );
            })}
            {hasBotoes ? (
              <th
                className={stickyActions ? styles.stickyActionHeader : undefined}
                style={getColumnWidth(columns.length, "Ações")}
              >
                Ações
              </th>
            ) : ""}
          </tr>
        </thead>
        {data.length > 0 ? (
          <tbody>
            {data.map((row, index) => (
              <Fragment key={index}>
                <tr className={styles.accordion_summary}>
                  {rows.map((i, indice) => {
                    const isSaldo = i === "saldo";
                    const alignStyle = isSaldo ? { textAlign: "right" } : { textAlign: "left" };
                    const widthStyle = getColumnWidth(indice, columns[indice]);
                    
                    return (
                      <td key={indice} style={{ ...alignStyle, ...widthStyle }}>
                        {row[i]}
                      </td>
                    );
                  })}
                  {row.botoes ? (
                    <td
                      className={stickyActions ? styles.stickyActionCell : undefined}
                      style={{ textAlign: "left", ...getColumnWidth(columns.length, "Ações") }}
                    >
                      <div className={styles.button_action_container}>
                        {React.isValidElement(row.botoes) ? (
                          row.botoes
                        ) : row.botoes.length === 1 ? (
                          <button
                            className={styles.button_action}
                            key={index}
                            onClick={row.botoes[0].onClick}
                          >
                            {row.botoes[0].icon && <span style={{ marginRight: ".5rem", display: "flex", alignItems: "center" }}>{row.botoes[0].icon}</span>}
                            {row.botoes[0].title}
                          </button>
                        ) : (
                          <button
                            className={styles.button_action}
                            onClick={() => handleRowClick(index)}
                          >
                            <TuneIcon sx={{ marginRight: ".5rem" }} />
                            Opções
                          </button>
                        )}
                      </div>
                    </td>
                  ) : (
                    ""
                  )}
                </tr>
                {expandedRow === index && Array.isArray(row.botoes) && row.botoes.length > 1 && (
                  <tr>
                    <td
                      colSpan={columns.length + 1}
                      className={styles.accordion_details}
                    >
                      <div className={styles.buttons}>
                        {row.botoes.map((item, index) => {
                          return (
                            <button
                              className={styles.btn}
                              key={index}
                              onClick={item.onClick}
                            >
                              <span>{item.icon}</span>
                              {item.title}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan={columns.length + (hasBotoes ? 1 : 0)} style={{ textAlign: "center", padding: "2rem" }}>
                <h3 className={styles.aviso_vazio}>Nenhum item encontrado!</h3>
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </>
  );
};

export default TableView;
